// ============================================================================
// BillingContext — centralised billing / entitlement state
// ----------------------------------------------------------------------------
// Fetches /api/billing/status ONCE (when auth resolves) and exposes it to the
// whole app. Also refreshed on demand via `refresh()`.
//
// Consumers:
//   • GlobalPaywallGuard  → redirects expired users to /pricing on any
//                            locked route (Dashboard, Library, Intervention…)
//   • Dashboard, Pricing  → drive UI (locks, banners, plan CTAs)
//
// Motivation: App Store Review requires the expired demo account to land on
// the paywall no matter which route it hits first. Previously we only ran
// the redirect inside Dashboard.jsx's useEffect, which was fragile — if the
// user deep-linked to /library/... or bounced between routes before billing
// resolved, they'd get stuck on a blurred locked page. Centralising the
// check here means every protected route enforces the paywall identically.
// ============================================================================

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingContext = createContext({
  billing: { status: null },
  loading: true,
  refresh: async () => {},
});

export const BillingProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [billing, setBilling] = useState({ status: null });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.user_id) {
      setBilling({ status: null });
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/billing/status`);
      setBilling(res.data || { status: null });
    } catch (error) {
      logger.error("Billing status fetch failed:", error);
      // Fail-open on network glitches — don't wall off a paying user because
      // /billing/status blipped. Paywall guard treats null/unknown as allowed.
      setBilling({ status: null });
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  // Listen for external `billing:refresh` events dispatched after actions
  // that change entitlement (promo redemption, RevenueCat purchase, etc.)
  // so paywall guards react immediately without a page reload.
  useEffect(() => {
    const handler = () => { refresh(); };
    window.addEventListener("billing:refresh", handler);
    return () => window.removeEventListener("billing:refresh", handler);
  }, [refresh]);

  return (
    <BillingContext.Provider value={{ billing, loading, refresh }}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => useContext(BillingContext);
